import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, useParams } from "react-router-dom";

import { useTran } from "@/hooks";
import { PATHS } from "@/lib/router";
import { showToast } from "@/lib/utils/toast";
import { useAuthStore } from "@/features/auth";
import { useOrganizationStore } from "@/features/organization";
import { ROLES } from "@/lib/constants/options";

import { surveysService } from "../services";
import { useSurveyFormStore, useSurveysStore } from "../hooks";
import { useCompanyStore } from "@/features/company";
import { buildSurveyPayload, validateSurveyForm } from "../utils/survey-form";

import QuestionLibrary from "./QuestionLibrary";
import SurveyQuestionDialog from "./SurveyQuestionDialog";
import SurveyDeleteQuestionDialog from "./SurveyDeleteQuestionDialog";
import SurveyDetailsCard from "./SurveyDetailsCard";
import SurveyFormHeader from "./SurveyFormHeader";
import SurveyQuestionsCard from "./SurveyQuestionsCard";

const SurveyForm = observer(({ mode = "standard" }) => {
  const { t } = useTran();
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    categories,
    lookupsLoaded,
    lookupsLoading,
    loadLookups,
    loadStandardSurveys,
    loadCustomSurveys,
  } = useSurveysStore();
  const organizationStore = useOrganizationStore();
  const { fetchPublicCompanies, companiesLoaded, companyOptions } =
    useCompanyStore();

  const formStore = useSurveyFormStore();

  const isEdit = Boolean(surveyId);
  const isStandard = mode === "standard";
  const isAdminRole = [
    ROLES.SYSTEM_ADMIN,
    ROLES.SUPER_ADMIN,
    ROLES.BUSINESS_UNIT,
  ].includes(user?.role);
  const canManage = isStandard ? isAdminRole : true;
  const isPublished = formStore.isPublished;

  const defaultCategory = categories?.[0] || "common";

  useEffect(() => {
    if (!lookupsLoaded && !lookupsLoading) {
      loadLookups();
    }
  }, [loadLookups, lookupsLoaded, lookupsLoading]);

  useEffect(() => {
    if (!user) return;
    organizationStore.fetchHierarchy(user.id).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchPublicCompanies().catch(() => {});
  }, [companiesLoaded, companyOptions.length, fetchPublicCompanies]);

  useEffect(() => {
    formStore.setContext({ mode, surveyId, defaultCategory });
  }, [formStore, mode, surveyId]);

  useEffect(() => {
    formStore.ensureCategory(categories, defaultCategory);
  }, [categories, defaultCategory, formStore]);

  useEffect(() => {
    if (!surveyId) return;
    let isActive = true;
    const loadSurvey = async () => {
      formStore.setLoadingSurvey(true);
      try {
        const fetcher = isStandard
          ? surveysService.getStandardSurvey
          : surveysService.getCustomSurvey;
        const data = await fetcher(surveyId);

        const payload =
          data?.survey || data?.data?.survey || data?.data || data;
        if (!payload || !isActive) return;
        formStore.applyLoadedSurvey(payload, defaultCategory);
      } catch (error) {
        showToast.error(error?.message || t("Unable to load survey."));
      } finally {
        if (isActive) formStore.setLoadingSurvey(false);
      }
    };

    loadSurvey();
    return () => {
      isActive = false;
    };
  }, [surveyId, isStandard, defaultCategory, t]);

  const handleFieldChange = formStore.updateField;

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (!canManage || isPublished) return;
    const validation = validateSurveyForm(formStore.form);
    if (!validation.valid) {
      showToast.error(t(validation.message));
      return;
    }

    formStore.setSubmitting(true);

    try {
      const payload = buildSurveyPayload(formStore.form);
      const action = isEdit
        ? isStandard
          ? surveysService.updateStandardSurvey
          : surveysService.updateCustomSurvey
        : isStandard
          ? surveysService.createStandardSurvey
          : surveysService.createCustomSurvey;

      const data = isEdit
        ? await action(surveyId, payload)
        : await action(payload);
      if (!data) {
        showToast.error(t("Unable to save survey."));
        return;
      }

      showToast.success(isEdit ? t("Survey updated.") : t("Survey created."));

      if (isStandard) {
        await loadStandardSurveys();
      } else {
        await loadCustomSurveys();
      }

      navigate(PATHS.SURVEYS);
    } catch (error) {
      showToast.error(error?.message || t("Unable to save survey."));
    } finally {
      formStore.setSubmitting(false);
    }
  };

  const selectedQuestionIds = useMemo(
    () =>
      formStore.form.questions
        .map((question) => question.originQuestionId || question.questionBankId)
        .filter(Boolean),
    [formStore.form.questions],
  );

  const companies = useMemo(() => {
    if (isStandard) {
      return companyOptions || [];
    }
    return organizationStore.items || [];
  }, [isStandard, companyOptions, organizationStore.items]);

  const isFormDisabled = !canManage || isPublished;

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <SurveyFormHeader
        canManage={canManage}
        isPublished={isPublished}
        submitting={formStore.submitting}
        loadingSurvey={formStore.loadingSurvey}
        isEdit={isEdit}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <SurveyDetailsCard
          form={formStore.form}
          isStandard={isStandard}
          disabled={isFormDisabled}
          companies={companies}
          companySearch={formStore.companySearch}
          onCompanySearchChange={formStore.setCompanySearch}
          onModeChange={(value) =>
            handleFieldChange("companySelectionMode", value)
          }
          onToggleCompany={(companyId) =>
            handleFieldChange(
              "selectedCompanyIds",
              formStore.form.selectedCompanyIds.includes(companyId)
                ? formStore.form.selectedCompanyIds.filter(
                    (id) => id !== companyId,
                  )
                : [...formStore.form.selectedCompanyIds, companyId],
            )
          }
          onFieldChange={handleFieldChange}
        />

        <SurveyQuestionsCard
          questions={formStore.form.questions}
          categories={categories}
          isStandard={isStandard}
          disabled={isFormDisabled}
          onOpenLibrary={() => formStore.setLibraryOpen(true)}
          onAddQuestion={() =>
            formStore.openQuestionDialog({ defaultCategory })
          }
          onEditQuestion={(index) =>
            formStore.openQuestionDialog({ index, defaultCategory })
          }
          onDeleteQuestion={(index) => formStore.setQuestionToDelete(index)}
        />
      </div>

      <SurveyQuestionDialog
        open={formStore.questionDialogOpen}
        onOpenChange={(open) => {
          formStore.setQuestionDialogOpen(open);
        }}
        initialQuestion={formStore.questionDraft}
        categories={categories}
        mode={isStandard ? "standard" : "custom"}
        onSave={formStore.saveQuestion}
      />

      <QuestionLibrary
        open={formStore.libraryOpen}
        onClose={() => formStore.setLibraryOpen(false)}
        categories={categories}
        selectedQuestionIds={selectedQuestionIds}
        onRemove={(questionId) => {
          formStore.setForm({
            ...formStore.form,
            questions: formStore.form.questions.filter(
              (question) =>
                question.originQuestionId !== questionId &&
                question.questionBankId !== questionId,
            ),
          });
        }}
        onSelect={(question) => {
          formStore.setForm({
            ...formStore.form,
            questions: [...formStore.form.questions, question],
          });
        }}
      />

      <SurveyDeleteQuestionDialog
        open={formStore.questionToDelete !== null}
        onOpenChange={(open) => {
          if (!open) formStore.setQuestionToDelete(null);
        }}
        onConfirm={formStore.deleteQuestion}
      />
    </form>
  );
});

export default SurveyForm;
